import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from "rxjs";
import { AppState } from 'src/app/store/app.reducer';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Ticket } from 'src/app/model/ticket';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import * as TicketActions from 'src/app/store/ticket/ticket.actions';
import { Site } from 'src/app/model/site';
import { selectSites } from 'src/app/store/site/site.selector';
import { selectAddSuccessTicket, selectAllTickets } from 'src/app/store/ticket/ticket.selector';
import * as moment from 'moment';
import { TICKETS_NEW_DEFAULT_EXPIRY_DAYS } from 'src/app/conf/support';
import { ShareDeviceDialogComponent } from 'src/app/modules/device/components/share-device-dialog/share-device.dialog.component';
import { StandardPageLayout, StandardNewValidator } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	templateUrl: './ticket.dialog.component.html',
})

export class TicketDialogComponent implements OnInit, OnDestroy {
	sites$: Observable<Site[] | undefined>;
	sitesSub: Subscription | undefined;
	addTicket$: Observable<boolean | undefined>;
	addTicketSub: Subscription | undefined;
	tickets$: Observable<Ticket[] | undefined>;
	ticketsSub: Subscription | undefined;

	ticket: Ticket = new Ticket();
	addTicketForm: FormGroup;
	tickets: Ticket[];

	sites: Site[] = [];
	minDate: Date = new Date();

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.store.dispatch(TicketActions.getAllTickets());
		this.subscribeAll();
		this.buildForm();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	initSelectors(): void {
		// Store selectors
		this.sites$ = this.store.select(selectSites);
		this.addTicket$ = this.store.select(selectAddSuccessTicket);
		this.tickets$ = this.store.select(selectAllTickets);
	}

	subscribeAll(): void {
		this.ticketsSub = this.tickets$?.subscribe(
			(tickets) => {
				this.tickets = tickets;
				this.enabledSite();
			}
		);
		this.sitesSub = this.sites$?.subscribe(
			(sites) => {
				this.sites = sites;
				this.enabledSite();
			}
		);
		this.addTicketSub = this.addTicket$?.subscribe(
			(addSuccess) => {
				if (addSuccess) {
					this.ref.close('success');
				}
			}
		);
	}

	enabledSite(): void {
		for (const site of this.sites) {
			const ticket = this.tickets.find(ti => !ti.FlagClosed && ti.SiteId === site.Id);
			if (site.TechnicianId === null || site.TechnicianId === undefined || ticket !== undefined || !site.CurrentlyOwned) {
				site.Disabled = true;
			} else {
				site.Disabled = false;
			}
		}
	}

	ngOnDestroy(): void {
		this.sitesSub?.unsubscribe();
		this.addTicketSub?.unsubscribe();
		this.ticketsSub?.unsubscribe();
	}

	buildForm(): void {
		this.ticket.TotalAccessExpDate = moment().add(TICKETS_NEW_DEFAULT_EXPIRY_DAYS, 'days').toDate();
		this.addTicketForm = new FormGroup({
			'note': new FormControl(this.ticket.Note, [Validators.required]),
			'accessDate': new FormControl(this.ticket.TotalAccessExpDate, [Validators.required]),
			'site': new FormControl(this.ticket.SiteId, [Validators.required]),
		});
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.addTicketForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.addTicketForm.get(key).errors;
			if (controlErrors != null) {
				Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				});
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const date = new Date(this.f.accessDate.value);
		const accessDateTime: Date = new Date(date.setHours(23, 59, 59));

		const ticket: Ticket = {
			...this.ticket,
			Note: this.f.note.value,
			FlagClosed: false,
			TotalAccess: true,
			SiteId: this.f.site.value.Id,
			TotalAccessExpDate: accessDateTime,
		};

		this.store.dispatch(TicketActions.addTicket({ ticket }));
	}

	get f(): any {
		return this.addTicketForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	justifyMessagesNewValidator(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: ShareDeviceDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorsConfirmFields(type: ShareDeviceDialogComponent["f"], classType: string, notEquivalentType: string): string {
		return StandardNewValidator.showErrorsConfirmFields(type, classType, notEquivalentType, this.screenWidth, this.addTicketForm);
	}

	showErrorRequiredMessage(type: ShareDeviceDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	showErrorConfirmInvalidMessage(type: ShareDeviceDialogComponent["f"], notEquivalentType: string): boolean {
		return StandardNewValidator.showErrorConfirmInvalidMessage(type, notEquivalentType, this.addTicketForm);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicInputTextDesktopTabletWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-49rem', 'w-25rem');
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}
}
